import { GroupOffersBy, GroupOffersByQuery, ShowHiddenOffersQuery as ShowHiddenOffersQuery, IOffersAdminResponse, TOffer } from "types/offers-temporary";
import http from "./http";
import { createLogger } from '../../utils/logger';
import type { SectionsListDeleteResult } from '../../ui/containers/lists/list-delete';
import { AppMode, DomainRegistrar, IApp, IDomain, IDomainBuyResult, IDomainsBuyRequestResponse, ISectionsList, IOffersSection, IUpsertAppRequest } from "@bprotsyk/aso-core";
const logger = createLogger('network');
const DEPLOY_STATUS = "delpoy_status";

// Функція для отримання WebSocket URL на основі базової URL з http
const getWebSocketUrl = (path: string): string => {
  // Отримуємо базовий URL з http
  const baseUrl = http.defaults.baseURL || '';
  
  // Визначаємо протокол для WebSocket
  let wsProtocol = 'ws://';
  if (baseUrl.startsWith('https://')) {
    wsProtocol = 'wss://';
  }
  
  // Видаляємо http:// або https:// з URL
  const urlWithoutProtocol = baseUrl
    .replace('https://', '')
    .replace('http://', '');
  
  // Видаляємо завершальний слеш з базового URL, якщо він є
  const cleanBaseUrl = urlWithoutProtocol.endsWith('/') 
    ? urlWithoutProtocol.slice(0, -1) 
    : urlWithoutProtocol;
  
  // Переконуємось, що шлях починається зі слеша
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // Отримуємо JWT токен з localStorage
  const token = localStorage.getItem('token');
  
  // Додаємо токен до URL як query параметр
  const urlWithToken = token ? `${cleanPath}?token=${encodeURIComponent(token)}` : cleanPath;
  
  // Повертаємо повний URL для WebSocket
  return `${wsProtocol}${cleanBaseUrl}${urlWithToken}`;
};

//TODO add  IDomainSetupResult (Cannot find name 'IDomainSetupResult')
interface IDomainSetupResult {
  succeed: string[],
  failed: string[]
}
// TODO add interface for IParams (aco-core)
interface IParamsAddDomain {
  domains: string[],
  ip: string,
}

interface IParamsBuyDomain {
  domains: string[];
  duration?: number;
  ip: string;
  provider?: DomainRegistrar;
}

interface IParamsEditDomain {
  domains: DNSRecord[];
  duration?: number;
  ip: string;
}

interface IParamsRenewDomain {
  domainName: string;
  years?: number;
  promotionCode?: string;
  isPremiumDomain?: boolean;
  premiumPrice?: number;
}


type DNSRecords = TXTRecord | MXRecord;
// Інтерфейс для TXT-записів
interface TXTRecord extends DNSRecord {
  type: "TXT";
}

// Інтерфейс для статичного розгортання
export interface IStaticDeploy {
  file: any,
  name: string,
}

// Інтерфейс для MX-записів
interface MXRecord extends DNSRecord {
  type: "MX";
  priority: number;
}

interface DNSRecord {
  type: string;
  value: string;
}

export interface IUpsertStubRequest {
  appId: number,
  name: string,
  templateUpsert: {
    idKey: string,
    gameNameKey: string,
    bgKey: string,
    fgKey: string,
    textKey: string,
  },
  jsonEdit: any,
  // offersStubParams removed - field no longer exists
  images: File[],
  treeData: Record<string, any>[]
}

export interface IWebviewEndpointLog {
  id: string;
  appId: number;
  domainName?: string | null;
  approach?: string | null;
  method: string;
  url: string;
  path: string;
  query?: Record<string, any>;
  body?: any;
  headers?: Record<string, string>;
  statusCode?: number | null;
  source?: string;
  createdAt: string;
}

export interface IClickDataRow {
  id: string;
  clickedAt: number;
  sessionId: string;
  app: { id: number; name: string; bundle: string; campaignId: number | null } | null;
  country: string | null;
  ip: string | null;
  platform: string | null;
  userAgent: string | null;
  stream: { id: number | null; name: string; position: number | null; key: string };
  offer: { id: number; name: string; geo: string | null; keitaroId: string | null } | null;
}

export interface IClickDataResponse {
  data: IClickDataRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
  apps: Array<{ id: number; name: string; bundle: string }>;
}

let appsWebSocket: WebSocket | null = null;
let appsCallbacks: Map<string, (apps: IApp[]) => void> = new Map();

let appsUpdateSubscribers: Set<(apps: IApp[]) => void> = new Set();


const subscribeToAppsUpdates = (callback: (apps: IApp[]) => void): () => void => {
  appsUpdateSubscribers.add(callback);
  
  connectAppsWebSocket('apps/ws/app-list');
  
  if (appsWebSocket && appsWebSocket.readyState === WebSocket.OPEN) {
    logger.info('Requesting initial app list on subscribe');
    appsWebSocket.send(JSON.stringify({
      type: 'getAppList',
      requestId: 'subscribe_' + Date.now().toString(),
    }));
  } else {
    // Якщо підключення не активне, запитуємо список після підключення
    logger.info('Will request data after connection established');
    setTimeout(() => {
      if (appsWebSocket && appsWebSocket.readyState === WebSocket.OPEN) {
        logger.info('Requesting initial app list after WebSocket connected');
        appsWebSocket.send(JSON.stringify({
          type: 'getAppList',
          requestId: 'subscribe_delayed_' + Date.now().toString(),
        }));
      }
    }, 1500);
  }
  
  return () => {
    appsUpdateSubscribers.delete(callback);
  };
};

const connectAppsWebSocket = (path: string) => {
  if (appsWebSocket && (appsWebSocket.readyState === WebSocket.OPEN || appsWebSocket.readyState === WebSocket.CONNECTING)) {
    logger.info(`WebSocket for ${path} already connected or connecting`);
    return;
  }

  const wsUrl = getWebSocketUrl(path);
  logger.info(`Creating new WebSocket connection: ${wsUrl}`);
  appsWebSocket = new WebSocket(wsUrl);

  appsWebSocket.onopen = () => {
    logger.info(`WebSocket connection for ${path} established`);
  };

  appsWebSocket.onmessage = (event) => {
    const message = JSON.parse(event.data);
    logger.debug(`WebSocket message received, type: ${message.type}`);
    
    switch (message.type) {
      case 'appListAllUpdated':
      case 'appListUpdated':
        logger.info(`App list update received: ${message.type}`);
        
        let appsArray: IApp[];
        if (Array.isArray(message.data)) {
          appsArray = message.data;
        } else if (typeof message.data === 'object' && message.data !== null) {
          appsArray = Object.values(message.data);
        } else {
          appsArray = [];
          logger.error('Unexpected data format from WebSocket', message.data);
        }
        
        if (appsCallbacks.size > 0) {
          logger.debug(`Dispatching data to ${appsCallbacks.size} callback(s)`);
        appsCallbacks.forEach((callback) => {
            callback(appsArray);
          });
        }
        
        if (appsUpdateSubscribers.size > 0) {
          logger.debug(`Dispatching data to ${appsUpdateSubscribers.size} subscribers`);
          appsUpdateSubscribers.forEach(subscriber => {
            subscriber(appsArray);
          });
        }
        break;
      
      case 'connected':
        logger.info('WebSocket connection confirmed');
        setTimeout(() => {
          if (appsWebSocket && appsWebSocket.readyState === WebSocket.OPEN) {
            logger.info('Requesting data after connection confirmed');
            appsWebSocket.send(JSON.stringify({
              type: 'getAppList',
              requestId: 'post_connected_' + Date.now().toString(),
            }));
          }
        }, 500);
        break;
        
      default:
        logger.debug(`Unhandled message type: ${message.type}`);
    }
  };

  appsWebSocket.onerror = (error) => {
    logger.error('WebSocket error', error);
  };

  appsWebSocket.onclose = () => {
    logger.info(`WebSocket connection for ${path} closed`);
    // Передаємо path під час повторного підключення
    setTimeout(() => connectAppsWebSocket(path), 3000);
  };
};

const getApps = async (filter?: string, activeOnly: boolean = true): Promise<IApp[]> => {
  return new Promise((resolve) => {
    const requestId = Date.now().toString();
    
    appsCallbacks.set(requestId, (apps: any) => {
      // Перевіряємо формат даних і забезпечуємо повернення масиву
      let appsArray: IApp[];
      
      if (Array.isArray(apps)) {
        appsArray = apps;
      } else if (typeof apps === 'object' && apps !== null) {
        // Якщо прийшов об'єкт, перетворюємо його на масив
        appsArray = Object.values(apps);
      } else {
        // Якщо прийшло щось інше, повертаємо порожній масив
        appsArray = [];
        logger.error('Unexpected data format from WebSocket', apps);
      }
      
      resolve(appsArray);
      appsCallbacks.delete(requestId);
    });
    
    connectAppsWebSocket('apps/ws/app-list');
    
    if (appsWebSocket && appsWebSocket.readyState === WebSocket.OPEN) {
      appsWebSocket.send(JSON.stringify({
        type: 'getAppList',
        requestId,
        filter,
        activeOnly
      }));
    } else {
      setTimeout(() => {
        if (appsWebSocket && appsWebSocket.readyState === WebSocket.OPEN) {
          appsWebSocket.send(JSON.stringify({
            type: 'getAppList',
            requestId,
            filter,
            activeOnly
          }));
        } else {
          appsCallbacks.delete(requestId);
          resolve([]);
        }
      }, 1000);
    }
  });
};




const getAllOffers = async (groupBy: string, showHidden: boolean, showActive: boolean) => {
  const response = await http.get<IOffersAdminResponse>(`offers?groupBy=${groupBy}&showHidden=${showHidden}`)

  return response.data
}

const getDomains = async (filter?: string) => {
  const response = await http.get<IDomain[]>(`/domains/${filter ? filter : ''}`);
  return response.data;
}

const getDomainBalance = async () => {
  const response = await http.get<number>(`/domains/balance`);
  return response.data;
}




const addDomain = async (params: IParamsAddDomain) => {
  try {
    const response = await http.post<IDomainSetupResult>(`/domains/add`, params);
    return response;
  } catch (error) {
    logger.error('Failed to add domain', error)
  }
}

const getBuyDomain = async (params: IParamsBuyDomain) => {
  try {
    const response = await http.post<IDomainsBuyRequestResponse>(`/domains/create-buy-request`, params);
    return response;
  } catch (error) {
    logger.error('Failed to get buy domain', error);
    throw error;
  }
}

const buyDomains = async (params: string) => {
  try {
    const response = await http.post<IDomainBuyResult>(`/domains/buy?requestId=${params}`);
    return response;
  } catch (error) {
    logger.error('Failed to buy domains', error);
    throw error;
  }
}

const editDomain = async (params: Partial<IDomain>) => {
  try {
    const response = await http.put<IDomain>(`/domains/edit`, params);
    return response;
  } catch (error) {
    logger.error('Failed to edit domain', error)
  }
}

const renewDomain = async (params: IParamsRenewDomain) => {
  try {
    const response = await http.post(`/domains/renew`, params);
    return response.data;
  } catch (error) {
    logger.error('Domain renewal error', error);
    throw error;
  }
}

export const uploadStatic = async (body: IStaticDeploy) => {
  try {
    const resolveUploadFile = (input: any): File | Blob | null => {
      if (!input) return null;

      if (Array.isArray(input)) {
        for (let i = input.length - 1; i >= 0; i -= 1) {
          const resolved = resolveUploadFile(input[i]);
          if (resolved) return resolved;
        }
        return null;
      }

      if (input.blobFile) {
        return resolveUploadFile(input.blobFile);
      }

      if (typeof Blob !== 'undefined' && input instanceof Blob) {
        return input;
      }

      return null;
    };

    const resolveUploadFilename = (input: any): string => {
      if (!input) return 'landing.zip';

      if (Array.isArray(input)) {
        for (let i = input.length - 1; i >= 0; i -= 1) {
          const resolved = resolveUploadFilename(input[i]);
          if (resolved) return resolved;
        }
        return 'landing.zip';
      }

      if (typeof input.name === 'string' && input.name.trim()) {
        return input.name.trim();
      }

      if (input.blobFile) {
        return resolveUploadFilename(input.blobFile);
      }

      return 'landing.zip';
    };

    const file = resolveUploadFile(body.file);
    if (!file) {
      throw new Error('No static archive file was provided');
    }

    const filename = resolveUploadFilename(body.file);
    const formData = new FormData();
    formData.append('name', body.name);
    formData.append('file', file, filename);
    logger.debug('uploadStatic payload', {
      domain: body.name,
      filename,
      size: typeof (file as any)?.size === 'number' ? (file as any).size : null,
      sourceType: Array.isArray(body.file) ? 'file-list' : typeof body.file,
    });
    const response = await http.post(`/domains/upload-files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (response.status == 200) {
      sessionStorage.setItem(DEPLOY_STATUS, "true");
    } else {
      sessionStorage.setItem(DEPLOY_STATUS, "false");
    }
    return response;
  } catch (error) {
    logger.error('Failed to upload static', error)
    sessionStorage.setItem(DEPLOY_STATUS, "false");
  }
}

// Sections Lists CRUD methods
const getSectionsLists = async () => {
  try {
    const response = await http.get<ISectionsList[]>('/offers/sections-lists');
    return response.data;
  } catch (error) {
    logger.error('Error fetching sections lists', error);
    throw error;
  }
}

const upsertSectionsList = async (sectionsList: ISectionsList) => {
  try {
    const response = await http.post<ISectionsList>('/offers/sections-lists', sectionsList);
    return response.data;
  } catch (error) {
    logger.error('Error upserting sections list', error);
    throw error;
  }
}

const deleteSectionsList = async (listId: number): Promise<SectionsListDeleteResult> => {
  try {
    const response = await http.delete(`/offers/sections-lists/${listId}`);
    return response.data;
  } catch (error) {
    logger.error('Error deleting sections list', error);
    throw error;
  }
}

// Sections CRUD methods
const getSections = async () => {
  try {
    const response = await http.get<IOffersSection[]>('/offers/sections');
    return response.data;
  } catch (error) {
    logger.error('Error fetching sections', error);
    throw error;
  }
}

const createSection = async (section: IOffersSection) => {
  try {
    const response = await http.post<IOffersSection>('/offers/sections', section);
    return response.data;
  } catch (error) {
    logger.error('Error creating section', error);
    throw error;
  }
}

const updateSection = async (sectionId: number, section: Partial<IOffersSection>) => {
  try {
    const response = await http.put<IOffersSection>(`/offers/sections/${sectionId}`, section);
    return response.data;
  } catch (error) {
    logger.error('Error updating section', error);
    throw error;
  }
}

const deleteSection = async (sectionId: number) => {
  try {
    const response = await http.delete(`/offers/sections/${sectionId}`);
    return response.data;
  } catch (error) {
    logger.error('Error deleting section', error);
    throw error;
  }
}

// Offer CRUD
const upsertOffer = async (offer: any, bgFile?: File, logoFile?: File) => {
  try {
    const formData = new FormData();
    formData.append('offer', JSON.stringify(offer));
    
    if (bgFile) {
      formData.append('bg', bgFile);
    }
    if (logoFile) {
      formData.append('logo', logoFile);
    }
    
    const response = await http.post('/offers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    logger.error('Error upserting offer', error);
    throw error;
  }
}


const upsertStub = async (body: IUpsertStubRequest) => {
  const formData = new FormData();

  // Додаємо файли до formData
  body.images?.forEach(file => {
    if (file) {
      formData.append('files[]', file);
    }
  });

  // Додаємо інші дані
  formData.append('appId', body?.appId?.toString() || "");
  formData.append('name', body.name);
  formData.append('templateUpsert', JSON.stringify(body.templateUpsert));
  formData.append('jsonEdit', JSON.stringify(body.jsonEdit));
  formData.append('treeData', JSON.stringify(body.treeData));

  // Перегляд вмісту FormData
  for (let pair of formData.entries()) {
    logger.debug('FormData entry', { key: pair[0], value: pair[1] });
  }

  try {
    const response = await http.post(`/apps/offer-stub/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    logger.error('Error uploading data and files', error);
    throw error;
  }
}

const upsertApp = async (app: IApp | IUpsertAppRequest) => {
  const response = await http.post<IApp>(`/apps/upsert`, app);
  return response;
}

const generateOfferwallPath = async (excludeAppId?: number): Promise<string> => {
  const response = await http.get('/apps/offerwall-path', {
    params: excludeAppId ? { excludeAppId } : undefined,
  });
  return String(response.data?.path || '');
}

const getAppStub = async (appId: number, name: string) => {
  const response = await http.get(`/apps/offer-stub/`, { params: { appId, name } });
  return response;
}


const getAppInfo = async (appId: number) => {
  const response = await http.get(`/apps/app-info/${appId}`);
  return response.data;
}

const updateAppStatuses = async (appStatuses: Array<{ appId: number; status: string }>) => {
  const response = await http.post('/apps/update-statuses', { appStatuses });
  return response.data;
};

const updatePlayStoreMonitoring = async (appId: number, enabled: boolean): Promise<IApp> => {
  const response = await http.post(`/apps/${appId}/play-store-monitoring`, { enabled });
  return response.data?.data || response.data;
};

const createPlayStoreWatchOnlyApp = async (payload: { name: string; bundle: string }): Promise<IApp> => {
  const response = await http.post('/apps/play-store-watch-only', payload);
  return response.data?.data || response.data;
};

const promotePlayStoreWatchOnlyApp = async (appId: number, appMode: AppMode): Promise<IApp> => {
  const response = await http.post(`/apps/${appId}/promote-watch-only`, { appMode });
  return response.data?.data || response.data;
};

const getKeitaroUnsynced = async () => {
  const response = await http.get('/offers/keitaro-unsynced');
  return response.data;
};

const importKeitaroOffers = async (offers: any[]) => {
  const response = await http.post('/offers/import-keitaro', { offers });
  return response.data;
};

const deleteOffer = async (offerId: number) => {
  const response = await http.delete(`/offers/delete/${offerId}`);
  return response.data;
};

const getAppxCampaigns = async () => {
  const response = await http.get('/apps/appx-campaigns');
  return response.data;
};

const syncAppxCampaigns = async () => {
  const response = await http.post('/apps/sync-appx-campaigns');
  return response.data;
};

const getUsedCampaignIds = async (): Promise<Array<{ appId: number; campaignId: number }>> => {
  const response = await http.get('/apps/used-campaign-ids');
  return response.data;
};

const getWebviewAvailability = async (): Promise<any> => {
  const response = await http.get('/apps/webview-availability');
  return response.data;
};

const getWebviewEndpointLogs = async (appId?: number, limit = 50): Promise<IWebviewEndpointLog[]> => {
  const params = appId ? { appId, limit } : { limit };
  const response = await http.get('/apps/webview-endpoint-logs', { params });
  return Array.isArray(response.data) ? response.data : (response.data?.data || []);
};

const getClickData = async (params: {
  appId?: number;
  page?: number;
  limit?: number;
  from?: number;
  search?: string;
} = {}): Promise<IClickDataResponse> => {
  const response = await http.get('/click-data', { params });
  return response.data;
};

const loadWebviewPolicyHtml = async (url: string): Promise<{ html: string; sourceUrl: string }> => {
  const response = await http.post('/apps/webview-policy-html/load', { url });
  return response.data?.data || response.data;
};

const getCaps = async (): Promise<any[]> => {
  const response = await http.get('/caps');
  return response.data;
};

const getCapsOffers = async (): Promise<any[]> => {
  const response = await http.get('/caps/offers');
  return response.data;
};

const createCap = async (payload: any): Promise<any> => {
  const response = await http.post('/caps', payload);
  return response.data;
};

const updateCap = async (id: string, payload: any): Promise<any> => {
  const response = await http.put(`/caps/${id}`, payload);
  return response.data;
};

const deleteCap = async (id: string): Promise<void> => {
  await http.delete(`/caps/${id}`);
};

export const PanelService = { getAllOffers, getDomains, getDomainBalance, addDomain, getBuyDomain, buyDomains, editDomain, renewDomain, getApps, uploadStatic, upsertApp, generateOfferwallPath, getAppStub, upsertStub, getAppInfo, subscribeToAppsUpdates, getSectionsLists, upsertSectionsList, deleteSectionsList, getSections, createSection, updateSection, deleteSection, updateAppStatuses, updatePlayStoreMonitoring, createPlayStoreWatchOnlyApp, promotePlayStoreWatchOnlyApp, upsertOffer, getKeitaroUnsynced, importKeitaroOffers, deleteOffer, getAppxCampaigns, syncAppxCampaigns, getUsedCampaignIds, getWebviewAvailability, getWebviewEndpointLogs, getClickData, loadWebviewPolicyHtml, getCaps, getCapsOffers, createCap, updateCap, deleteCap }
